import { Form, Formik } from 'formik';
import { QRCodeSVG } from 'qrcode.react';
import { useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { MultiFactorSetupResponse } from '../../../typings/MultiFactor.interface';
import { GlobalContext } from '../../contexts/GlobalContext';
import Button, { ClickableArea } from '../base/Button';
import { StyledCard, StyledCardSection } from '../base/Card';
import Text from '../base/Text';
import CopyableField from '../CopyableField';
import TextInput from '../Form/Fields/TextInput';
import { Div } from '../helpers/StyledUtils';
import Modal from '../Modal';
import { useToasts } from '../Toast';
interface Props {
  onClose: () => void;
  onSuccess: (backup_codes?: string[]) => void;
  has_password?: boolean;
  /** Modal width; defaults to medium. */
  size?: React.ComponentProps<typeof Modal>['size'];
}
const StyledQRCodeContainer = styled.div(({
  theme
}) => css`
    display: flex;
    justify-content: center;
    background-color: white;
    border-radius: ${theme.radius.normal};
    margin: 0 auto;
    width: fit-content;
  `);
const AuthenticatorSetup: React.FC<Props> = ({
  onClose,
  onSuccess,
  has_password,
  size = 'medium'
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const [setup_data, setSetupData] = useState<MultiFactorSetupResponse | null>(null);
  const [is_loading, setIsLoading] = useState(false);
  const [show_manual_entry, setShowManualEntry] = useState(false);
  const [is_initializing, setIsInitializing] = useState(!has_password); // Only auto-init if no password required

  // Create authenticator - either on mount (no password) or after password verification
  const handleCreateAuthenticator = async (password?: string) => {
    setIsLoading(true);
    try {
      const data = await HookdeckAPI.multi_factor.addAuthenticator(password);
      setSetupData(data);
    } catch (e: any) {
      addToast('error', e.response?.message || 'Failed to create authenticator');
      if (!has_password) {
        // Only close on mount-time errors for OAuth users
        onClose();
      }
    } finally {
      setIsLoading(false);
      setIsInitializing(false);
    }
  };

  // Initialize authenticator on mount (only if no password required)
  const has_initialized = useRef(false);
  useEffect(() => {
    if (!has_password && !has_initialized.current) {
      has_initialized.current = true;
      handleCreateAuthenticator();
    }
  }, [handleCreateAuthenticator, has_password]);
  const handleVerify = async (values: {
    code: string;
    password?: string;
  }) => {
    // If password required and no setup_data yet, create authenticator first
    if (has_password && !setup_data && values.password) {
      await handleCreateAuthenticator(values.password);
      return;
    }
    if (!setup_data) return;
    setIsLoading(true);
    try {
      const response = await HookdeckAPI.multi_factor.verifyAuthenticator(setup_data.id, values.code);
      addToast('success', 'Authenticator verified and activated');
      onSuccess(response.backup_codes);
    } catch (e_0: any) {
      addToast('error', e_0.response?.message || 'Invalid code. Please try again.');
      setIsLoading(false);
    }
  };
  return <Formik initialValues={{
    code: '',
    password: ''
  }} validate={values_0 => {
    const errors: {
      code?: string;
      password?: string;
    } = {};
    // Password validation (only required in first step for users with password)
    if (has_password && !setup_data) {
      if (!values_0.password) {
        errors.password = 'Password is required';
      }
    }
    // Code validation (only required in second step, after setup_data exists)
    if (setup_data) {
      if (!values_0.code) {
        errors.code = 'Code is required';
      } else if (!/^\d{6}$/.test(values_0.code)) {
        errors.code = 'Code must be 6 digits';
      }
    }
    return errors;
  }} onSubmit={values_1 => handleVerify(values_1)}>
      {props => {
      // Determine which step we're on
      const is_password_step = has_password && !setup_data;
      return <Modal title="Add authenticator app" size={size} portal onClose={onClose} onCancel={onClose} cancel_label="Cancel" submit_label={is_password_step ? 'Continue' : 'Verify'} is_submitting={is_loading} submit_disabled={is_password_step ? !props.values.password || !!props.errors.password : is_initializing || !setup_data || !props.isValid || !props.values.code} onSubmit={props.submitForm}>
            <Form>
              {is_password_step ?
          // Step 1: Password verification (for users with password)
          <>
                  <Text m={{
              b: 4
            }}>
                    For your security, please confirm your password to add a new authenticator app.
                  </Text>
                  <TextInput label="Password" name="password" type="password" auto_complete="current-password" required focus />
                </> :
          // Step 2: QR code scanning and verification
          <>
                  <Text m={{
              b: 4
            }}>
                    Scan this QR code with your authenticator app, then enter the 6-digit code to
                    verify. If you’re unable to scan, you can also copy the secret below and paste
                    it in your app.
                  </Text>

                  {setup_data && <StyledQRCodeContainer>
                      <StyledCard p={4}>
                        <QRCodeSVG value={setup_data.totp_uri} size={200} level="M" />
                      </StyledCard>
                    </StyledQRCodeContainer>}

                  <Div m={{
              y: 4
            }}>
                    <TextInput label="Verification Code" name="code" placeholder="000000" required focus={!!setup_data} maxlength={6} format={v => v.replace(/[^0-9]/g, '')} />
                  </Div>

                  {setup_data && <StyledCard dashed={!show_manual_entry} overflow_hidden>
                      <StyledCardSection muted={show_manual_entry}>
                        <ClickableArea p={{
                  x: 1.5,
                  y: 1.5,
                  l: 3
                }} onClick={() => setShowManualEntry(!show_manual_entry)}>
                          <Div flex={{
                    justify: 'space-between'
                  }}>
                            <Text>Unable to scan?</Text>
                            <Button small ghost icon={show_manual_entry ? 'chevron_up' : 'chevron_down'} />
                          </Div>
                        </ClickableArea>
                      </StyledCardSection>

                      {show_manual_entry && <StyledCardSection muted p={{
                x: 3,
                y: 3,
                t: 2
              }}>
                          <Text size="xs" muted m={{
                  b: 1
                }}>
                            Paste this secret key in your app
                          </Text>
                          <CopyableField value={setup_data.secret_for_manual_entry} mono />
                        </StyledCardSection>}
                    </StyledCard>}
                </>}
            </Form>
          </Modal>;
    }}
    </Formik>;
};
export default AuthenticatorSetup;